import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, input } from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { isEqual } from 'lodash-es';
import { SaveForLaterExportsModule } from 'src/app/extensions/save-for-later/exports/save-for-later-exports.module';

import { ProductContextDirective } from 'ish-core/directives/product-context.directive';
import { CheckoutFacade } from 'ish-core/facades/checkout.facade';
import { ProductContextFacade } from 'ish-core/facades/product-context.facade';
import { LineItemUpdate } from 'ish-core/models/line-item-update/line-item-update.model';
import { LineItemView } from 'ish-core/models/line-item/line-item.model';
import { OrderLineItem } from 'ish-core/models/order/order.model';
import { PricePipe } from 'ish-core/models/price/price.pipe';
import { ServerSettingPipe } from 'ish-core/pipes/server-setting.pipe';
import { BasketPromotionComponent } from 'ish-shared/components/basket/basket-promotion/basket-promotion.component';
import { LineItemEditComponent } from 'ish-shared/components/line-item/line-item-edit/line-item-edit.component';
import { LineItemWarrantyComponent } from 'ish-shared/components/line-item/line-item-warranty/line-item-warranty.component';
import { ProductBundleDisplayComponent } from 'ish-shared/components/product/product-bundle-display/product-bundle-display.component';
import { ProductIdComponent } from 'ish-shared/components/product/product-id/product-id.component';
import { ProductImageComponent } from 'ish-shared/components/product/product-image/product-image.component';
import { ProductInventoryComponent } from 'ish-shared/components/product/product-inventory/product-inventory.component';
import { ProductNameComponent } from 'ish-shared/components/product/product-name/product-name.component';
import { ProductQuantityLabelComponent } from 'ish-shared/components/product/product-quantity-label/product-quantity-label.component';
import { ProductQuantityComponent } from 'ish-shared/components/product/product-quantity/product-quantity.component';
import { ProductShipmentComponent } from 'ish-shared/components/product/product-shipment/product-shipment.component';
import { ProductVariationDisplayComponent } from 'ish-shared/components/product/product-variation-display/product-variation-display.component';
import { CustomEditLineItemComponent } from 'ish-shared/dias/components/basket/custom-edit-line-item/custom-edit-line-item.component';

import { OrderTemplatesExportsModule } from '../../../../extensions/order-templates/exports/order-templates-exports.module';
import { WishlistsExportsModule } from '../../../../extensions/wishlists/exports/wishlists-exports.module';

@Component({
  selector: 'ish-line-item-list-element',
  templateUrl: './line-item-list-element.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ProductImageComponent,
    ProductNameComponent,
    ProductIdComponent,
    ProductVariationDisplayComponent,
    ProductBundleDisplayComponent,
    ProductInventoryComponent,
    LineItemEditComponent,
    ProductContextDirective,
    ProductShipmentComponent,
    NgbPopover,
    FaIconComponent,
    BasketPromotionComponent,
    OrderTemplatesExportsModule,
    WishlistsExportsModule,
    ProductQuantityLabelComponent,
    ProductQuantityComponent,
    LineItemWarrantyComponent,
    PricePipe,
    ServerSettingPipe,
    SaveForLaterExportsModule,
    TranslateModule,
    CustomEditLineItemComponent,
  ],
})
export class LineItemListElementComponent implements OnInit {
  private context = inject(ProductContextFacade);
  private checkoutFacade = inject(CheckoutFacade);

  readonly pli = input.required<Partial<LineItemView & OrderLineItem>>();
  readonly editable = input(true);
  readonly lineItemViewType = input<'simple' | 'availability'>(undefined);
  readonly productSku = input<string>();

  ngOnInit() {
    this.context.hold(this.context.validDebouncedQuantityUpdate$(), quantity => {
      this.checkoutFacade.updateBasketItem({ itemId: this.pli().id, quantity });
    });
  }

  get oldPrice() {
    const pli = this.pli();
    return isEqual(pli.singleBasePrice, pli.undiscountedSingleBasePrice) ? undefined : pli.undiscountedSingleBasePrice;
  }

  onUpdateItem(update: LineItemUpdate) {
    this.checkoutFacade.updateBasketItem(update);
  }

  onDeleteItem(itemId: string) {
    this.checkoutFacade.deleteBasketItem(itemId);
  }
}
